import { useEffect, useRef } from 'react';
import samplesSelectorModel from '@/components/Samples/SamplesSelector/SamplesSelectorModel';
import { useSearchParams } from 'next/navigation';
import { useManagerEntity } from '@/shared/managerHelpers';
import etaLoader from '@/lib/EtaLoader';

// Wait for an element to exist in the DOM
function waitForElement(selector: string, timeout: number = 2000): Promise<Element> {
  return new Promise((resolve, reject) => {
    // Check if element already exists
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }

    // Set up MutationObserver to watch for the element
    const observer = new MutationObserver((mutations, obs) => {
      const element = document.querySelector(selector);
      if (element) {
        obs.disconnect();
        resolve(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Set timeout to avoid waiting forever
    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Timeout waiting for element: ${selector}`));
    }, timeout);
  });
}

// select sample if in url we have somethine like this:
// practice?m=ID
// or
// play?m=ID
export function useSampleSelection() {
  const searchParams = useSearchParams()
  const sampleId = searchParams.get('m')
  const manager = useManagerEntity();
  const isLoadingRef = useRef(false);

  useEffect(() => {
    // Only proceed when BOTH sampleId exists AND manager is fully initialized
    // manager being non-null means 'managerInited' event has already fired
    if (sampleId && manager) {
      // Prevent duplicate loading
      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;

      // Clear any previous error state when attempting to load a new sample
      if (etaLoader.hasError) {
        etaLoader.clearError();
      }

      // Wait for renderer-sheet to exist before loading sample
      // This handles the timing issue when navigating to Practice page
      waitForElement('#renderer-sheet')
        .then(() => {
          samplesSelectorModel.selectSample(sampleId);
        })
        .catch((err) => {
          console.error('[useSampleSelection] Failed to find renderer-sheet:', err);
        })
        .finally(() => {
          isLoadingRef.current = false;
        });
    }
  }, [sampleId, manager]);
} 